import React, { useState, useCallback } from 'react';
import { Flex, Text, Button, Grid, Card } from '@radix-ui/themes';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import styles from './OnboardingModal.module.scss';
import { Dialog } from "@/components/shared/dialog";
import { useSignup } from '@/api/signup/signup.api';
import { useAuth0 } from '@auth0/auth0-react';
import { OnboardingData, OnboardingModalProps, REFERRAL_OPTIONS } from './OnboardingModal.types';
import { SignupTicketingSystems } from '@/api/signup/signup.model';
import { integrationNameToGalleryItem } from '@/api/integration/integrationNameToGalleryItem';
import { IntegrationName } from '@/api/integration/Integration.model';

export function OnboardingModal({ isOpen }: OnboardingModalProps) {
    const { mutateAsync: signup, isLoading } = useSignup();
    const { getAccessTokenSilently, user } = useAuth0();
    const [formData, setFormData] = useState<OnboardingData>({
        companyName: '',
        referralSource: '',
        objectives: '',
        existingTicketingSystem: undefined,
    });
    const [errors, setErrors] = useState<Partial<OnboardingData>>({});

    const validateForm = useCallback(() => {
        const newErrors: Partial<OnboardingData> = {};

        if (!formData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }

        if (!formData.referralSource) {
            newErrors.referralSource = 'Please select how you heard about us';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    const handleInputChange = useCallback((field: keyof OnboardingData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    }, [errors]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm() || !user?.sub || !user?.email) {
            return;
        }

        try {
            const response = await signup({
                userAuth0Id: user.sub,
                email: user.email,
                companyName: formData.companyName,
                referralSource: formData.referralSource,
                objectives: formData.objectives ?? '',
                existingTicketingSystem: formData.existingTicketingSystem,
            });
            if (response.success) {
                await getAccessTokenSilently({ cacheMode: 'off' });
                window.location.reload();
            } else {
                console.error('Failed to submit onboarding data');
            }
        } catch (error) {
            console.error('Failed to submit onboarding data:', error);
        }
    }, [formData, validateForm, signup, getAccessTokenSilently, user]);

    return (
        <Dialog open={isOpen} onOpenChange={() => { }}>
            <Dialog.Content size="large" className={styles.modalContent}>
                <Flex height="100%" width="100%">
                    <Flex direction="column" className={styles.leftPanel}>
                        <div className={styles.formHeader}>
                            <Text className={styles.mainTitle}>
                                Tell us about your company
                            </Text>
                        </div>

                        <form onSubmit={handleSubmit} className={styles.onboardingForm}>
                            <div className={styles.formFields}>
                                <Flex direction="column" gap="2">
                                    <Text size="3" weight="medium">
                                        Company Name
                                    </Text>
                                    <Input
                                        type="text"
                                        placeholder="Acme Inc."
                                        value={formData.companyName}
                                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                                        data-error={errors.companyName}
                                    />
                                    {errors.companyName && (
                                        <Text size="1" color="red">
                                            {errors.companyName}
                                        </Text>
                                    )}
                                </Flex>

                                <Flex direction="column" gap="2">
                                    <Text size="3" weight="medium">
                                        How did you hear about us?
                                    </Text>
                                    <Flex className={styles.referralButtons}>
                                        {REFERRAL_OPTIONS.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`${styles.referralButton} ${formData.referralSource === option.value ? styles.selected : ''}`}
                                                onClick={() => handleInputChange('referralSource', option.value)}
                                            >
                                                <span>{option.label}</span>
                                            </button>
                                        ))}
                                    </Flex>
                                    {errors.referralSource && (
                                        <Text size="1" color="red">
                                            {errors.referralSource}
                                        </Text>
                                    )}
                                </Flex>

                                <div className={styles.textareaContainer}>
                                    <Text size="3" weight="medium">
                                        What are you trying to achieve? (Optional)
                                    </Text>
                                    <Textarea
                                        placeholder="Describe your goals and what you're looking to accomplish with Quack..."
                                        value={formData.objectives}
                                        onChange={(e) => handleInputChange('objectives', e.target.value)}
                                        className={styles.textarea}
                                    />
                                </div>

                                <Flex direction="column" gap="2">
                                    <Text size="3" weight="medium">
                                        Which ticketing system are you currently using? (Optional)
                                    </Text>
                                    <Grid columns="8" gap="2" display="inline-grid" width={"100%"} height={"fit-content"} >
                                        {Object.values(SignupTicketingSystems).map((option) => {
                                            const integration = integrationNameToGalleryItem[option as IntegrationName];
                                            if (!integration) {
                                                return null;
                                            }
                                            const { title, svg } = integration;

                                            return (
                                                <Card
                                                    key={option}
                                                    size="1"
                                                    onClick={() => handleInputChange('existingTicketingSystem', option)}
                                                    className={`${styles.existingTicketingSystemButton} ${formData.existingTicketingSystem === option ? styles.selected : ''}`}
                                                >
                                                    <Flex direction={"column"} gap={"5px"}>
                                                        <Flex gap={"5px"} justify={"between"} >
                                                            <div>{svg}</div>
                                                        </Flex>

                                                        <Text size={"2"} weight={"medium"} as={"div"}>
                                                            {title}
                                                        </Text>
                                                    </Flex>
                                                </Card>
                                                
                                            )
                                        })}
                                    </Grid>
                                </Flex>
                            </div>

                            <Flex justify="end">
                                <Button
                                    highContrast
                                    size="3"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Setting up...' : 'Continue'}
                                </Button>
                            </Flex>
                        </form>
                    </Flex>
                </Flex>
            </Dialog.Content>
        </Dialog>
    );
}
